import React, { useMemo } from 'react';
import SiteCharts from '../../../components/SiteCharts';
import { sgNbAdditionCellChartConfig } from '../cellChartConfigs';

/**
 * Props for the SgNbAdditionCell component
 */
interface SgNbAdditionCellProps {
  /** Cell identifier passed to API as cell_name parameter */
  cellName: string;
  /** Callback function to toggle fullscreen mode */
  onFullscreenToggle: () => void;
  /** Current fullscreen state */
  isFullscreen: boolean;
}

/**
 * SgNB Addition Success Rate Chart (LTE Cell Level)
 * 
 * Displays Secondary gNodeB (SgNB) addition success rate metrics for individual LTE cells:
 * - SgNB Addition Attempt (count)
 * - SgNB Addition Success (count)
 * - SgNB Addition Success Rate (percentage)
 * 
 * This component is a thin wrapper around the SiteCharts template component,
 * configured with cell-level SgNB addition metrics from cellChartConfigs.ts
 * 
 * @component
 * @example
 * ```tsx
 * <SgNbAdditionCell
 *   cellName="173716_145"
 *   onFullscreenToggle={() => setFullscreen(!fullscreen)}
 *   isFullscreen={false}
 * />
 * ```
 * 
 * @see {@link ../cellChartConfigs.ts} - Chart configuration (sgNbAdditionCellChartConfig)
 * @see {@link ../../../components/SiteCharts.tsx} - Template component
 * @see API: /api/lte-cell/sgnb-addition?cell_name={cellName}
 */

/**
 * SgNB Addition Success Rate Chart (Cell Level)
 * 
 * Displays SgNB (Secondary gNodeB) addition attempts, ACK success count, and success rate
 * for a specific LTE cell. SgNB addition is part of the ENDC setup procedure.
 */
const SgNbAdditionCell: React.FC<SgNbAdditionCellProps> = ({ 
  cellName, 
  onFullscreenToggle, 
  isFullscreen 
}) => {
  // Memoize additionalParams to prevent infinite re-renders
  const additionalParams = useMemo(() => ({ cell_name: cellName }), [cellName]);

  return (
    <SiteCharts 
      config={sgNbAdditionCellChartConfig}
      onFullscreenToggle={onFullscreenToggle}
      isFullscreen={isFullscreen}
      additionalParams={additionalParams}
    />
  );
};

export default SgNbAdditionCell;
